import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import {
  addDevice, armDevice, disarmDevice,
  getCanFields, getDevices, getVehicleConfigs, removeDevice,
  updateVehicleConfig, deleteDeviceHistory,
  CanFieldInfo, VehicleConfigOut,
} from '../services/api';
import Header from '../components/Layout/Header';
import { parseUtc } from '../utils/dataAge';
import './VehicleSettings.css';

// ── Constants ──────────────────────────────────────────────────────────────────

const DEVICE_TYPES = [
  { value: '',                  label: '— nie ustawiono —' },
  { value: 'FMC920',            label: 'FMC920' },
  { value: 'FMC150',            label: 'FMC150' },
  { value: 'FMC234',            label: 'FMC234' },
  { value: 'Horta Signal Mini', label: 'Horta Signal Mini' },
  { value: 'Horta Shark',       label: 'Horta Shark' },
  { value: 'Albatross S8',      label: 'Albatross S8' },
  { value: 'Albatross S10.3',   label: 'Albatross S10.3' },
  { value: 'Albatross S10.5',   label: 'Albatross S10.5' },
  { value: 'Inne',              label: 'Inne' },
];

const CATEGORIES = [
  { value: '',           label: '— brak —' },
  { value: 'car',        label: 'Samochód osobowy' },
  { value: 'van',        label: 'Dostawczy' },
  { value: 'truck',      label: 'Ciężarowy' },
  { value: 'tipper',     label: 'Wywrotka' },
  { value: 'tractor',    label: 'Ciągnik' },
  { value: 'motorcycle', label: 'Motocykl' },
  { value: 'bicycle',    label: 'Rower' },
];

const DIN_ICONS = [
  { value: '',                   label: '— brak —' },
  { value: 'ti-snowflake',       label: 'Lodówka / chłodnia' },
  { value: 'ti-droplet',         label: 'Pompa' },
  { value: 'ti-bulb',            label: 'Oświetlenie' },
  { value: 'ti-arrow-bar-up',    label: 'Podnośnik' },
  { value: 'ti-lock',            label: 'Blokada silnika' },
  { value: 'ti-elevator',        label: 'Winda' },
  { value: 'ti-air',             label: 'Sprężarka' },
  { value: 'ti-cpu',             label: 'Agregat' },
  { value: 'ti-wind',            label: 'Wentylator' },
  { value: 'ti-bell',            label: 'Alarm' },
  { value: 'ti-temperature',     label: 'Chłodnia (temp.)' },
  { value: 'ti-tool',            label: 'Pompa hydrauliczna' },
  { value: 'ti-sun',             label: 'Oświetlenie robocze' },
];

const FUEL_SOURCES = [
  { value: 'auto',       label: 'auto (wykryj źródło)' },
  { value: 'percentage', label: 'Poziom % (io89 / fuelLevel)' },
  { value: 'liters',     label: 'Litry (io84 / CAN)' },
];

// ── Types ──────────────────────────────────────────────────────────────────────

interface Row extends VehicleConfigOut {
  category: string | null;
}

interface AddForm {
  name: string; uniqueid: string; model: string; category: string;
}

interface DinEntry { pin: number; type?: 'in' | 'out'; label: string; icon: string; }
interface W1Entry  { slot: number; label: string; icon: string; }

interface EditModal {
  row: Row;
  activeTab: 'basic' | 'fuel' | 'can' | 'io';
  // Tab 1 — Podstawowe
  device_name: string;
  registration_number: string;
  device_type: string;
  phone: string;
  category: string;
  has_tachograph: boolean;
  notes: string;
  // Tab 2 — Paliwo i przebieg
  tank_capacity_liters: string;
  fuel_alert_threshold_liters: string;
  fuel_source: string;
  odometer_gps_offset: string;
  speed_limit_kmh: string;
  // Tab 3 — CAN
  canFields: CanFieldInfo[];
  canLoading: boolean;
  canError: string | null;
  // Tab 4 — I/O (proste piny FMC/Inne)
  din2_label: string;  din2_icon: string;
  din3_label: string;  din3_icon: string;
  dout1_label: string; dout1_icon: string;
  dout2_label: string; dout2_icon: string;
  // Tab 4 — I/O (złożone Horta)
  din_config: DinEntry[];
  wire1_labels: W1Entry[];
  etoll_enabled: boolean;
  tacho_ddd_enabled: boolean;
  soms_enabled: boolean;
  // Meta
  saving: boolean;
  error: string | null;
}

interface ClearModal {
  deviceId: number; deviceName: string; loading: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function idLabel(dt: string | null): string {
  if (dt === 'FMC150' || dt === 'FMC920') return 'IMEI';
  if (dt === 'Horta Signal Mini' || dt === 'Horta Shark') return 'SN';
  return 'ID';
}

function initDinConfig(dt: string, existing: DinEntry[]): DinEntry[] {
  if (dt === 'Horta Signal Mini') {
    return [1, 2].map(pin => ({
      pin,
      type: (existing.find(e => e.pin === pin)?.type ?? 'in') as 'in' | 'out',
      label: existing.find(e => e.pin === pin)?.label ?? '',
      icon:  existing.find(e => e.pin === pin)?.icon  ?? '',
    }));
  }
  if (dt === 'Horta Shark') {
    return [1, 2, 3, 4].map(pin => ({
      pin,
      label: existing.find(e => e.pin === pin)?.label ?? '',
      icon:  existing.find(e => e.pin === pin)?.icon  ?? '',
    }));
  }
  return existing;
}

function initWire1(dt: string, existing: W1Entry[]): W1Entry[] {
  const n = dt === 'Horta Shark' ? 32 : dt === 'Horta Signal Mini' ? 4 : 0;
  if (!n) return existing;
  return Array.from({ length: n }, (_, i) => ({
    slot:  i + 1,
    label: existing.find(e => e.slot === i + 1)?.label ?? '',
    icon:  existing.find(e => e.slot === i + 1)?.icon  ?? '',
  }));
}

// ── Component ──────────────────────────────────────────────────────────────────

const VehicleSettings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.administrator ?? false;

  const [rows, setRows]             = useState<Row[]>([]);
  const [loading, setLoading]       = useState(true);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [search, setSearch]         = useState('');

  const [editModal, setEditModal]   = useState<EditModal | null>(null);
  const [clearModal, setClearModal] = useState<ClearModal | null>(null);
  const [savedId, setSavedId]       = useState<number | null>(null);

  const [showAdd, setShowAdd]       = useState(false);
  const [addForm, setAddForm]       = useState<AddForm>({ name: '', uniqueid: '', model: '', category: 'car' });
  const [addError, setAddError]     = useState<string | null>(null);
  const [addSaving, setAddSaving]   = useState(false);

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [armingIds, setArmingIds]   = useState<Set<number>>(new Set());
  const [contextMenuRow, setContextMenuRow] = useState<Row | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    load();
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    setGlobalError(null);
    try {
      const [devices, configs] = await Promise.all([getDevices(), getVehicleConfigs()]);
      const devMap = new Map(devices.map(d => [d.id, d]));
      setRows(configs.map(cfg => ({ ...cfg, category: devMap.get(cfg.device_id)?.category ?? null })));
    } catch {
      setGlobalError('Nie udało się załadować danych');
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.device_name.toLowerCase().includes(q) ||
      r.uniqueid.toLowerCase().includes(q) ||
      (r.registration_number ?? '').toLowerCase().includes(q) ||
      (r.device_type ?? '').toLowerCase().includes(q) ||
      (r.phone ?? '').toLowerCase().includes(q) ||
      ((r as any).client_id ?? '').toString().toLowerCase().includes(q),
    );
  }, [rows, search]);

  // ── Edit modal ───────────────────────────────────────────────────────────────

  const openModal = async (row: Row) => {
    const dt = row.device_type ?? '';
    const existing_dc = (row.din_config ?? []) as DinEntry[];
    const existing_w1 = (row.wire1_labels ?? []) as W1Entry[];
    setEditModal({
      row,
      activeTab: 'basic',
      device_name:                 row.device_name,
      registration_number:         row.registration_number ?? '',
      device_type:                 dt,
      phone:                       row.phone ?? '',
      category:                    row.category ?? '',
      has_tachograph:              row.has_tachograph,
      notes:                       row.notes ?? '',
      tank_capacity_liters:        row.tank_capacity_liters != null ? String(row.tank_capacity_liters) : '',
      fuel_alert_threshold_liters: row.fuel_alert_threshold_liters != null ? String(row.fuel_alert_threshold_liters) : '',
      fuel_source:                 row.fuel_source,
      odometer_gps_offset:         String(row.odometer_gps_offset ?? 0),
      speed_limit_kmh:             row.speed_limit_kmh != null ? String(row.speed_limit_kmh) : '',
      canFields: [], canLoading: true, canError: null,
      din2_label:  row.din2_label  ?? '', din2_icon:  row.din2_icon  ?? '',
      din3_label:  row.din3_label  ?? '', din3_icon:  row.din3_icon  ?? '',
      dout1_label: row.dout1_label ?? '', dout1_icon: row.dout1_icon ?? '',
      dout2_label: row.dout2_label ?? '', dout2_icon: row.dout2_icon ?? '',
      din_config:    initDinConfig(dt, existing_dc),
      wire1_labels:  initWire1(dt, existing_w1),
      etoll_enabled:     row.etoll_enabled     ?? false,
      tacho_ddd_enabled: row.tacho_ddd_enabled ?? false,
      soms_enabled:      row.soms_enabled      ?? false,
      saving: false, error: null,
    });
    try {
      const fields = await getCanFields(row.device_id);
      setEditModal(prev => prev ? { ...prev, canFields: fields, canLoading: false } : null);
    } catch {
      setEditModal(prev => prev ? { ...prev, canLoading: false, canError: 'Nie udało się pobrać pól CAN' } : null);
    }
  };

  const closeModal = () => { if (!editModal?.saving) setEditModal(null); };

  const setM = (field: keyof EditModal, value: any) =>
    setEditModal(prev => prev ? { ...prev, [field]: value } : null);

  const setDeviceType = (newType: string) =>
    setEditModal(prev => {
      if (!prev) return null;
      const existing_dc = prev.din_config;
      const existing_w1 = prev.wire1_labels;
      return {
        ...prev,
        device_type:  newType,
        din_config:   initDinConfig(newType, existing_dc),
        wire1_labels: initWire1(newType, existing_w1),
      };
    });

  const toggleCanField = (key: string, enabled: boolean) =>
    setEditModal(prev => prev
      ? { ...prev, canFields: prev.canFields.map(f => f.key === key ? { ...f, enabled } : f) }
      : null);

  const setDinEntry = (pin: number, updates: Partial<DinEntry>) =>
    setEditModal(prev => prev
      ? { ...prev, din_config: prev.din_config.map(e => e.pin === pin ? { ...e, ...updates } : e) }
      : null);

  const setW1Entry = (slot: number, updates: Partial<W1Entry>) =>
    setEditModal(prev => prev
      ? { ...prev, wire1_labels: prev.wire1_labels.map(e => e.slot === slot ? { ...e, ...updates } : e) }
      : null);

  const saveModal = async () => {
    if (!editModal) return;
    setM('saving', true);
    setM('error', null);
    try {
      const dt = editModal.device_type;
      const can_display_fields = editModal.canFields.length > 0
        ? Object.fromEntries(editModal.canFields.map(f => [f.key, f.enabled]))
        : editModal.row.can_display_fields ?? {};

      const payload = {
        name:                        editModal.device_name.trim() || undefined,
        category:                    editModal.category || null,
        device_type:                 dt || null,
        phone:                       editModal.phone.trim() || null,
        registration_number:         editModal.registration_number.trim() || null,
        tank_capacity_liters:        editModal.tank_capacity_liters !== '' ? parseFloat(editModal.tank_capacity_liters) : null,
        fuel_alert_threshold_liters: editModal.fuel_alert_threshold_liters !== '' ? parseFloat(editModal.fuel_alert_threshold_liters) : null,
        fuel_source:                 editModal.fuel_source,
        speed_limit_kmh:             editModal.speed_limit_kmh !== '' ? parseInt(editModal.speed_limit_kmh, 10) : null,
        notes:                       editModal.notes.trim() || null,
        has_tachograph:              editModal.has_tachograph,
        armed_threshold_m:           editModal.row.armed_threshold_m,
        odometer_gps_offset:         editModal.odometer_gps_offset !== '' ? parseFloat(editModal.odometer_gps_offset) : 0,
        can_display_fields,
        din2_label:  editModal.din2_label  || null,
        din2_icon:   editModal.din2_icon   || null,
        din3_label:  editModal.din3_label  || null,
        din3_icon:   editModal.din3_icon   || null,
        dout1_label: editModal.dout1_label || null,
        dout1_icon:  editModal.dout1_icon  || null,
        dout2_label: editModal.dout2_label || null,
        dout2_icon:  (dt === 'FMC150' || dt === 'Inne' || !dt) ? (editModal.dout2_icon || null) : null,
        etoll_enabled:     editModal.etoll_enabled,
        tacho_ddd_enabled: editModal.tacho_ddd_enabled,
        soms_enabled:      editModal.soms_enabled,
        wire1_labels:      editModal.wire1_labels,
        din_config:        editModal.din_config,
      };

      const updated = await updateVehicleConfig(editModal.row.device_id, payload);
      setRows(prev => prev.map(r =>
        r.device_id === editModal.row.device_id
          ? { ...r, ...updated, device_name: updated.device_name, category: editModal.category || null }
          : r
      ));
      setEditModal(null);
      setSavedId(editModal.row.device_id);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setSavedId(null), 2000);
    } catch (e: any) {
      setEditModal(prev => prev
        ? { ...prev, saving: false, error: e?.response?.data?.detail || 'Błąd zapisu' }
        : null);
    }
  };

  // ── Clear history ────────────────────────────────────────────────────────────

  const openClear = (row: Row) =>
    setClearModal({ deviceId: row.device_id, deviceName: row.device_name, loading: false });

  const confirmClear = async () => {
    if (!clearModal) return;
    setClearModal(prev => prev ? { ...prev, loading: true } : null);
    try {
      await deleteDeviceHistory(clearModal.deviceId);
      setClearModal(null);
    } catch (e: any) {
      setGlobalError(e?.response?.data?.detail || 'Błąd czyszczenia danych');
      setClearModal(null);
    }
  };

  // ── Alarm ────────────────────────────────────────────────────────────────────

  const toggleArm = async (row: Row) => {
    setArmingIds(prev => new Set(prev).add(row.device_id));
    try {
      const updated = row.armed ? await disarmDevice(row.device_id) : await armDevice(row.device_id);
      setRows(prev => prev.map(r => r.device_id === row.device_id ? { ...r, ...updated } : r));
    } catch (e: any) {
      setGlobalError(e?.response?.data?.detail || 'Błąd operacji alarmu');
    } finally {
      setArmingIds(prev => { const s = new Set(prev); s.delete(row.device_id); return s; });
    }
  };

  // ── Add device ───────────────────────────────────────────────────────────────

  const openAdd = () => {
    setAddForm({ name: '', uniqueid: '', model: '', category: 'car' });
    setAddError(null);
    setShowAdd(true);
  };

  const submitAdd = async () => {
    setAddError(null);
    if (!addForm.name.trim())     { setAddError('Podaj nazwę urządzenia'); return; }
    if (!addForm.uniqueid.trim()) { setAddError('Podaj IMEI / uniqueId'); return; }
    setAddSaving(true);
    try {
      await addDevice({
        name:     addForm.name.trim(),
        uniqueid: addForm.uniqueid.trim(),
        model:    addForm.model.trim() || undefined,
        category: addForm.category || undefined,
      });
      setShowAdd(false);
      await load();
    } catch (e: any) {
      setAddError(e?.response?.data?.detail || 'Błąd dodawania urządzenia');
    } finally {
      setAddSaving(false);
    }
  };

  // ── Delete device ────────────────────────────────────────────────────────────

  const confirmDelete = async (row: Row) => {
    if (!window.confirm(`Czy na pewno usunąć urządzenie „${row.device_name}"?\nTa operacja jest nieodwracalna.`)) return;
    setDeletingId(row.device_id);
    try {
      await removeDevice(row.device_id);
      await load();
    } catch (e: any) {
      setGlobalError(e?.response?.data?.detail || 'Błąd usuwania urządzenia');
    } finally {
      setDeletingId(null);
    }
  };

  // ── I/O tab content ──────────────────────────────────────────────────────────

  const renderIOTab = () => {
    if (!editModal) return null;
    const dt = editModal.device_type;

    const IconSelect = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
      <select className="vm-select" value={value} onChange={e => onChange(e.target.value)}>
        {DIN_ICONS.map(ic => <option key={ic.value} value={ic.value}>{ic.label}</option>)}
      </select>
    );

    const CanBadges = ({ cans }: { cans: string[] }) => (
      <div className="vm-can-row">
        {cans.length > 0
          ? cans.map(c => <span key={c} className="vm-can-badge">{c}</span>)
          : <span className="vm-can-badge vm-can-badge--none">Brak CAN</span>}
      </div>
    );

    if (!dt) {
      return (
        <div className="vm-io-empty">
          Ustaw typ urządzenia w zakładce <strong>Podstawowe</strong>, aby skonfigurować wejścia/wyjścia.
        </div>
      );
    }

    if (dt === 'FMC150' || dt === 'Inne') {
      return <>
        <div className="vm-section-title">Wejścia/Wyjścia cyfrowe</div>
        {[
          { pin: 'DIN2',  lbl: editModal.din2_label,  ico: editModal.din2_icon,  onLbl: (v: string) => setM('din2_label', v),  onIco: (v: string) => setM('din2_icon', v) },
          { pin: 'DIN3',  lbl: editModal.din3_label,  ico: editModal.din3_icon,  onLbl: (v: string) => setM('din3_label', v),  onIco: (v: string) => setM('din3_icon', v) },
          { pin: 'DOUT1', lbl: editModal.dout1_label, ico: editModal.dout1_icon, onLbl: (v: string) => setM('dout1_label', v), onIco: (v: string) => setM('dout1_icon', v) },
          { pin: 'DOUT2', lbl: editModal.dout2_label, ico: editModal.dout2_icon, onLbl: (v: string) => setM('dout2_label', v), onIco: (v: string) => setM('dout2_icon', v) },
        ].map(({ pin, lbl, ico, onLbl, onIco }) => (
          <div key={pin} className="vm-io-row">
            <span className="vm-io-pin">{pin}</span>
            <input type="text" className="vm-input" placeholder="Etykieta…" maxLength={50} value={lbl} onChange={e => onLbl(e.target.value)} />
            <IconSelect value={ico} onChange={onIco} />
          </div>
        ))}
        {dt === 'FMC150' && <CanBadges cans={['CAN1', 'CAN2']} />}
      </>;
    }

    if (dt === 'FMC920') {
      return <>
        <div className="vm-section-title">Wejście analogowe i wyjście cyfrowe</div>
        <div className="vm-io-row">
          <span className="vm-io-pin">AIN1</span>
          <input type="text" className="vm-input" placeholder="Etykieta…" maxLength={50}
            value={editModal.din2_label} onChange={e => setM('din2_label', e.target.value)} />
          <IconSelect value={editModal.din2_icon} onChange={v => setM('din2_icon', v)} />
        </div>
        <div className="vm-io-row">
          <span className="vm-io-pin">DOUT1</span>
          <input type="text" className="vm-input" placeholder="Etykieta…" maxLength={50}
            value={editModal.dout1_label} onChange={e => setM('dout1_label', e.target.value)} />
          <IconSelect value={editModal.dout1_icon} onChange={v => setM('dout1_icon', v)} />
        </div>
        <CanBadges cans={[]} />
      </>;
    }

    if (dt === 'Horta Signal Mini') {
      return <>
        <div className="vm-section-title">Piny konfigurowalne (2×)</div>
        {editModal.din_config.map(e => (
          <div key={e.pin} className="vm-io-row vm-io-row--4col">
            <span className="vm-io-pin">Pin {e.pin}</span>
            <select className="vm-select" value={e.type ?? 'in'}
              onChange={ev => setDinEntry(e.pin, { type: ev.target.value as 'in' | 'out' })}>
              <option value="in">Wejście</option>
              <option value="out">Wyjście</option>
            </select>
            <input type="text" className="vm-input" placeholder="Etykieta…" maxLength={50}
              value={e.label} onChange={ev => setDinEntry(e.pin, { label: ev.target.value })} />
            <IconSelect value={e.icon} onChange={v => setDinEntry(e.pin, { icon: v })} />
          </div>
        ))}

        <div className="vm-section-title" style={{ marginTop: 16 }}>Sloty 1-Wire Signal (4×)</div>
        <div className="vm-wire1-grid">
          {editModal.wire1_labels.map(e => (
            <div key={e.slot} className="vm-io-row">
              <span className="vm-io-pin">Slot {e.slot}</span>
              <input type="text" className="vm-input" placeholder="np. Temperatura kabiny" maxLength={50}
                value={e.label} onChange={ev => setW1Entry(e.slot, { label: ev.target.value })} />
              <IconSelect value={e.icon} onChange={v => setW1Entry(e.slot, { icon: v })} />
            </div>
          ))}
        </div>

        <div className="vm-section-title" style={{ marginTop: 16 }}>Funkcje</div>
        <div className="vm-checks">
          <label className="vm-check-row"><input type="checkbox" checked={editModal.etoll_enabled}     onChange={e => setM('etoll_enabled', e.target.checked)} /><span>eToll</span></label>
          <label className="vm-check-row" title="Odczyt danych z tachografu nie jest dziś obsługiwany — planowane na przyszłość, bez efektu w aplikacji"><input type="checkbox" checked={editModal.has_tachograph} disabled onChange={e => setM('has_tachograph', e.target.checked)} /><span>Tachograf (dane podstawowe) — planowane, brak dziś efektu</span></label>
        </div>
        <CanBadges cans={['CAN1']} />
      </>;
    }

    if (dt === 'Horta Shark') {
      return <>
        <div className="vm-section-title">Wejścia cyfrowe DIN (4×)</div>
        {editModal.din_config.map(e => (
          <div key={e.pin} className="vm-io-row">
            <span className="vm-io-pin">DIN {e.pin}</span>
            <input type="text" className="vm-input" placeholder="Etykieta…" maxLength={50}
              value={e.label} onChange={ev => setDinEntry(e.pin, { label: ev.target.value })} />
            <IconSelect value={e.icon} onChange={v => setDinEntry(e.pin, { icon: v })} />
          </div>
        ))}

        <div className="vm-section-title" style={{ marginTop: 16 }}>Wyjście i LIN/K-Line</div>
        <div className="vm-io-row">
          <span className="vm-io-pin">OUT</span>
          <input type="text" className="vm-input" placeholder="Etykieta…" maxLength={50}
            value={editModal.dout1_label} onChange={e => setM('dout1_label', e.target.value)} />
          <IconSelect value={editModal.dout1_icon} onChange={v => setM('dout1_icon', v)} />
        </div>
        <div className="vm-io-row vm-io-row--2col">
          <span className="vm-io-pin">LIN/K-Line</span>
          <input type="text" className="vm-input" placeholder="Etykieta…" maxLength={50}
            value={editModal.dout2_label} onChange={e => setM('dout2_label', e.target.value)} />
        </div>

        <div className="vm-section-title" style={{ marginTop: 16 }}>Sloty 1-Wire Signal (do 32×)</div>
        <div className="vm-wire1-grid">
          {editModal.wire1_labels.map(e => (
            <div key={e.slot} className="vm-io-row">
              <span className="vm-io-pin">Slot {e.slot}</span>
              <input type="text" className="vm-input" placeholder="np. Temperatura" maxLength={50}
                value={e.label} onChange={ev => setW1Entry(e.slot, { label: ev.target.value })} />
              <IconSelect value={e.icon} onChange={v => setW1Entry(e.slot, { icon: v })} />
            </div>
          ))}
        </div>

        <div className="vm-section-title" style={{ marginTop: 16 }}>Funkcje</div>
        <div className="vm-checks">
          <label className="vm-check-row"><input type="checkbox" checked={editModal.etoll_enabled}     onChange={e => setM('etoll_enabled', e.target.checked)} /><span>eToll</span></label>
          <label className="vm-check-row" title="Odczyt danych z tachografu nie jest dziś obsługiwany — planowane na przyszłość, bez efektu w aplikacji"><input type="checkbox" checked={editModal.has_tachograph} disabled onChange={e => setM('has_tachograph', e.target.checked)} /><span>Tachograf (dane podstawowe) — planowane, brak dziś efektu</span></label>
          <label className="vm-check-row"><input type="checkbox" checked={editModal.tacho_ddd_enabled} onChange={e => setM('tacho_ddd_enabled', e.target.checked)} /><span>Tachograf — pobieranie plików DDD</span></label>
          <label className="vm-check-row"><input type="checkbox" checked={editModal.soms_enabled}      onChange={e => setM('soms_enabled', e.target.checked)} /><span>SoMS</span></label>
        </div>
        <CanBadges cans={['CAN1', 'CAN2']} />
      </>;
    }

    return <div className="vm-io-empty">Brak konfigurowalnych wejść/wyjść dla wybranego typu urządzenia.</div>;
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  const TAB_LABELS: Record<EditModal['activeTab'], string> = {
    basic: 'Podstawowe',
    fuel:  'Paliwo i przebieg',
    can:   'CAN',
    io:    'Wejścia/Wyjścia',
  };

  return (
    <div className="settings-layout">
      <Header />
      <div className="settings-body">
        <div className="settings-container">

          <div className="settings-title-bar">
            <h1 className="settings-title">Konfiguracja pojazdów</h1>
            <button className="btn-add-device" onClick={openAdd}>+ Dodaj urządzenie</button>
          </div>

          {globalError && <div className="settings-error">{globalError}</div>}

          {!loading && (
            <div className="settings-search-bar">
              <input type="text" className="settings-search"
                placeholder="Szukaj po nazwie, IMEI, nr rej., typie urządzenia, nr SIM lub kliencie…"
                value={search} onChange={e => setSearch(e.target.value)} />
              {search && <button className="search-clear" onClick={() => setSearch('')}>✕</button>}
            </div>
          )}

          {loading ? (
            <div className="settings-loading">Ładowanie…</div>
          ) : (
            <div className="settings-table-wrapper">
              <table className="settings-table">
                <thead>
                  <tr>
                    <th>Nazwa pojazdu</th>
                    <th>ID urządzenia</th>
                    <th>Typ urządzenia</th>
                    <th>Nr rej.</th>
                    <th className="actions-th">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="settings-empty">
                        {search ? 'Brak wyników dla podanej frazy' : 'Brak pojazdów'}
                      </td>
                    </tr>
                  )}
                  {filteredRows.map(row => (
                    <tr key={row.device_id}>
                      <td className="device-name">
                        {row.device_name}
                        {savedId === row.device_id && <span className="saved-badge"> ✓</span>}
                      </td>
                      <td className="uid-cell">
                        <span className="uid-label">{idLabel(row.device_type)}</span>
                        {row.uniqueid}
                      </td>
                      <td>{row.device_type || <span className="dim">—</span>}</td>
                      <td>{row.registration_number || <span className="dim">—</span>}</td>
                      <td className="actions-cell">
                        {/* Desktop: przyciski akcji */}
                        <div className="desktop-actions">
                          <button className="btn-edit" onClick={() => openModal(row)}>Edytuj</button>
                          <button className="btn-clear-history" onClick={() => openClear(row)}
                            title="Wyczyść zebrane dane historyczne">Wyczyść</button>
                          {isAdmin && (
                            <button className="btn-delete"
                              disabled={deletingId === row.device_id}
                              onClick={() => confirmDelete(row)}
                              title="Usuń urządzenie z systemu">
                              {deletingId === row.device_id ? '…' : 'Usuń'}
                            </button>
                          )}
                          <button
                            className={row.armed ? 'btn-disarm' : 'btn-arm'}
                            title={row.armed
                              ? `Rozbrój (próg ${row.armed_threshold_m} m)`
                              : `Uzbrój (próg ${row.armed_threshold_m} m)`}
                            disabled={armingIds.has(row.device_id)}
                            onClick={() => toggleArm(row)}
                          >
                            {armingIds.has(row.device_id) ? '…' : row.armed ? '🔒' : '🔓'}
                          </button>
                          {row.armed && row.armed_at && (
                            <span className="armed-since">
                              {parseUtc(row.armed_at).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        {/* Mobile: ⋮ przycisk otwierający bottom sheet */}
                        <button className="btn-context-menu"
                          onClick={e => { e.stopPropagation(); setContextMenuRow(row); }}>
                          ⋮
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Modal: edycja ── */}
      {editModal && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="vm-modal" onClick={e => e.stopPropagation()}>

            <div className="vm-modal-header">
              <div className="vm-modal-title">
                <span className="vm-modal-name">{editModal.device_name || editModal.row.device_name}</span>
                {editModal.registration_number && (
                  <span className="vm-modal-reg">{editModal.registration_number}</span>
                )}
              </div>
              <button className="vm-modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="vm-tabs">
              {(['basic', 'fuel', 'can', 'io'] as const).map(tab => {
                const disabled = tab === 'io' && !editModal.device_type;
                return (
                  <button key={tab}
                    className={`vm-tab${editModal.activeTab === tab ? ' active' : ''}${disabled ? ' vm-tab--disabled' : ''}`}
                    disabled={disabled}
                    onClick={() => !disabled && setM('activeTab', tab)}
                  >
                    {TAB_LABELS[tab]}
                  </button>
                );
              })}
            </div>

            <div className="vm-modal-body">
              {editModal.error && <div className="vm-modal-error">{editModal.error}</div>}

              {/* ── Tab: Podstawowe ── */}
              {editModal.activeTab === 'basic' && (
                <div className="vm-fields">
                  <label className="vm-field">
                    <span>Nazwa pojazdu</span>
                    <input className="vm-input" type="text" maxLength={100}
                      value={editModal.device_name}
                      onChange={e => setM('device_name', e.target.value)} />
                  </label>
                  <label className="vm-field">
                    <span>Nr rejestracyjny</span>
                    <input className="vm-input" type="text" maxLength={20}
                      value={editModal.registration_number}
                      onChange={e => setM('registration_number', e.target.value)} />
                  </label>
                  <label className="vm-field">
                    <span>Typ urządzenia</span>
                    <select className="vm-select" value={editModal.device_type}
                      onChange={e => setDeviceType(e.target.value)}>
                      {DEVICE_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
                    </select>
                  </label>
                  <label className="vm-field">
                    <span>Nr SIM</span>
                    <input className="vm-input" type="text" maxLength={20} placeholder="48XXXXXXXXX"
                      value={editModal.phone}
                      onChange={e => setM('phone', e.target.value)} />
                  </label>
                  <label className="vm-field">
                    <span>Kategoria pojazdu</span>
                    <select className="vm-select" value={editModal.category}
                      onChange={e => setM('category', e.target.value)}>
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </label>
                  <label className="vm-field vm-field--checkbox" title="Odczyt danych z tachografu nie jest dziś obsługiwany — planowane na przyszłość, bez efektu w aplikacji">
                    <input type="checkbox" checked={editModal.has_tachograph} disabled
                      onChange={e => setM('has_tachograph', e.target.checked)} />
                    <span>Tachograf (planowane — bez efektu dziś)</span>
                  </label>
                  <label className="vm-field">
                    <span>Notatki</span>
                    <textarea className="vm-textarea" rows={3} maxLength={500}
                      value={editModal.notes}
                      onChange={e => setM('notes', e.target.value)} />
                  </label>
                </div>
              )}

              {/* ── Tab: Paliwo i przebieg ── */}
              {editModal.activeTab === 'fuel' && (
                <div className="vm-fields">
                  <label className="vm-field">
                    <span>Pojemność zbiornika (L)</span>
                    <input className="vm-input" type="number" min={0} max={9999} step={0.1}
                      value={editModal.tank_capacity_liters}
                      onChange={e => setM('tank_capacity_liters', e.target.value)} />
                  </label>
                  <label className="vm-field">
                    <span>Alert paliwa (L)</span>
                    <input className="vm-input" type="number" min={0} max={9999} step={0.1} placeholder="5.0"
                      value={editModal.fuel_alert_threshold_liters}
                      onChange={e => setM('fuel_alert_threshold_liters', e.target.value)} />
                  </label>
                  <label className="vm-field">
                    <span>Źródło paliwa</span>
                    <select className="vm-select" value={editModal.fuel_source}
                      onChange={e => setM('fuel_source', e.target.value)}>
                      {FUEL_SOURCES.map(fs => <option key={fs.value} value={fs.value}>{fs.label}</option>)}
                    </select>
                  </label>
                  <label className="vm-field">
                    <span>Offset przebiegu GPS (km)</span>
                    <input className="vm-input" type="number" min={0} step={1} placeholder="np. 2427"
                      title="Przebieg GPS urządzenia przy montażu"
                      value={editModal.odometer_gps_offset}
                      onChange={e => setM('odometer_gps_offset', e.target.value)} />
                  </label>
                  <label className="vm-field">
                    <span>Limit prędkości (km/h)</span>
                    <input className="vm-input" type="number" min={1} max={300} step={1}
                      value={editModal.speed_limit_kmh}
                      onChange={e => setM('speed_limit_kmh', e.target.value)} />
                  </label>
                </div>
              )}

              {/* ── Tab: CAN ── */}
              {editModal.activeTab === 'can' && (
                <div className="vm-can-tab">
                  {editModal.canLoading && <div className="vm-can-loading">Pobieranie pól CAN…</div>}
                  {editModal.canError  && <div className="vm-modal-error">{editModal.canError}</div>}
                  {!editModal.canLoading && !editModal.canError && editModal.canFields.length === 0 && (
                    <div className="vm-can-empty">Brak wykrytych pól CAN dla tego urządzenia.</div>
                  )}
                  {editModal.canFields.map(f => (
                    <label key={f.key} className="vm-can-field">
                      <input type="checkbox" checked={f.enabled}
                        onChange={e => toggleCanField(f.key, e.target.checked)} />
                      <span className="vm-can-field-label">{f.label}</span>
                      {f.last_value !== null && (
                        <span className="vm-can-field-value">{f.last_value}</span>
                      )}
                      <span className="vm-can-field-key">{f.key}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* ── Tab: Wejścia/Wyjścia ── */}
              {editModal.activeTab === 'io' && (
                <div className="vm-io-tab">{renderIOTab()}</div>
              )}
            </div>

            <div className="vm-modal-footer">
              <div className="vm-modal-footer-right">
                <button className="btn-cancel" onClick={closeModal} disabled={editModal.saving}>Anuluj</button>
                <button className="btn-save" onClick={saveModal} disabled={editModal.saving}>
                  {editModal.saving ? 'Zapisywanie…' : 'Zapisz'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: wyczyść dane ── */}
      {clearModal && (
        <div className="modal-backdrop" onClick={() => !clearModal.loading && setClearModal(null)}>
          <div className="settings-modal" onClick={e => e.stopPropagation()}>
            <h3>Wyczyść dane urządzenia</h3>
            <p className="modal-warn-text">
              Czy na pewno chcesz usunąć wszystkie zebrane dane historyczne
              dla urządzenia <strong>{clearModal.deviceName}</strong>?
              Operacja jest nieodwracalna.
            </p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setClearModal(null)} disabled={clearModal.loading}>Anuluj</button>
              <button className="btn-danger" onClick={confirmClear} disabled={clearModal.loading}>
                {clearModal.loading ? 'Usuwanie…' : 'Tak, usuń wszystko'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: dodaj urządzenie ── */}
      {showAdd && (
        <div className="modal-backdrop" onClick={() => setShowAdd(false)}>
          <div className="settings-modal" onClick={e => e.stopPropagation()}>
            <h3>Dodaj urządzenie</h3>
            {addError && <div className="settings-error">{addError}</div>}
            <label className="modal-field">
              <span>Nazwa *</span>
              <input type="text" className="cell-input" placeholder="np. Ford Transit 123"
                value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} />
            </label>
            <label className="modal-field">
              <span>IMEI / UniqueId *</span>
              <input type="text" className="cell-input" placeholder="15-cyfrowy numer IMEI"
                value={addForm.uniqueid} onChange={e => setAddForm(f => ({ ...f, uniqueid: e.target.value }))} />
            </label>
            <label className="modal-field">
              <span>Model urządzenia</span>
              <input type="text" className="cell-input" placeholder="np. FMC150"
                value={addForm.model} onChange={e => setAddForm(f => ({ ...f, model: e.target.value }))} />
            </label>
            <label className="modal-field">
              <span>Kategoria</span>
              <select className="cell-select" value={addForm.category}
                onChange={e => setAddForm(f => ({ ...f, category: e.target.value }))}>
                {CATEGORIES.filter(c => c.value !== '').map(c =>
                  <option key={c.value} value={c.value}>{c.label}</option>
                )}
              </select>
            </label>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowAdd(false)}>Anuluj</button>
              <button className="btn-save" onClick={submitAdd} disabled={addSaving}>
                {addSaving ? 'Dodawanie…' : 'Dodaj'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile: context menu (bottom sheet) ── */}
      {contextMenuRow && (
        <>
          <div className="ctx-backdrop" onClick={() => setContextMenuRow(null)} />
          <div className="ctx-bottom-sheet">
            <div className="ctx-sheet-title">{contextMenuRow.device_name}</div>
            <button className="ctx-item"
              onClick={() => { openModal(contextMenuRow); setContextMenuRow(null); }}>
              ✏️ Edytuj
            </button>
            <button className="ctx-item"
              onClick={() => { openClear(contextMenuRow); setContextMenuRow(null); }}>
              🗑️ Wyczyść dane
            </button>
            {isAdmin && (
              <button className="ctx-item ctx-item--danger"
                disabled={deletingId === contextMenuRow.device_id}
                onClick={() => { confirmDelete(contextMenuRow); setContextMenuRow(null); }}>
                ❌ Usuń urządzenie
              </button>
            )}
            <button className="ctx-item"
              disabled={armingIds.has(contextMenuRow.device_id)}
              onClick={() => { toggleArm(contextMenuRow); setContextMenuRow(null); }}>
              {contextMenuRow.armed ? '🔒 Rozbrój alarm' : '🔓 Uzbrój alarm'}
            </button>
            <button className="ctx-item ctx-item--cancel"
              onClick={() => setContextMenuRow(null)}>
              Anuluj
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default VehicleSettings;
